import { createReducer, on, ActionReducerMap } from '@ngrx/store';
import * as ChartActions from './chart.actions';
import * as ChartSwitchActions from './chart-switch.actions';
import * as TempActions from '../temp.actions';

import { Map, OrderedSet } from 'immutable';
import { EMPTY_ENCODING, Encoding } from '@mode-vegas/vegas';

import { DEFAULT_CHART_FORMAT, DEFAULT_FLAMINGO_PAGE_SIZE, FLAMINGO_RESERVED_REFS } from '@mode/shared/util-js';
import { ChartTypes } from '@mode/shared/contract-common';
import { immRemoveKey, isPresent } from '@mode/shared/util-js';
import {
  loadChartFormat,
  loadChartFormatFromMappedChart,
  makeChartHistoryStack,
  makeChartHistoryStackFromMappedChart,
  popUndoStack,
  popRedoStack,
  pushUndoStack,
  replaceLastHistory,
} from '../chart-helpers';
import { EncodingHelpers, FieldFormatHelpers } from '@mode/charts/util-vegas';
import { InjectionToken } from '@angular/core';

export const chartFeatureKey = 'chart';

type ChartFeatureState = ChartTypes.ChartFeatureState;
type ChartFormat = ChartTypes.ChartFormat;

// Making a limited action union so we don't have to import every action in the system
type Actions = ChartActions.Union | TempActions.Union | ChartSwitchActions.Union;

export const initialState: ChartFeatureState = {
  selectedToken: null,

  tokens: OrderedSet<string>(),
  versions: Map<string, 1 | 2>(),
  queryTokens: Map<string, string>(),
  switchViewTokens: Map<string, string>(),
  resources: Map<string, ChartTypes.ChartResource>(),
  createdTimestamps: Map<string, string>(),
  titles: Map<string, string>(),
  descriptions: Map<string, string>(),
  captions: Map<string, ChartTypes.CaptionInfo>(),
  chartTypes: Map<string, ChartTypes.ChartType>(),
  encodings: Map<string, Encoding>(),
  formats: Map<string, ChartFormat>(),
  fieldFormats: Map<string, ChartTypes.FieldFormatMap>(),
  fieldColors: Map<string, ChartTypes.FieldColorMap>(),
  colorPaletteTokens: Map<string, string>(),
  facetSizes: Map<string, ChartTypes.FacetSizeMap>(),
  isAutoUpdateDisabled: Map<string, boolean>(),
  manuallyApplyChanges: Map<string, boolean>(),
  unappliedChangesCount: Map<string, number>(),
  ephemerals: Map<string, boolean>(),

  v1Views: Map<string, any>(),
  histories: Map<string, ChartTypes.ChartHistory>(),

  tablePaging: {},
  tableColumnSearch: {},

  selectedFieldToFormat: null,
};

function pushToHistory(
  history = initialState.histories,
  token: string,
  next: Partial<ChartTypes.ChartSnapshot>,
  action: string
) {
  const entry = history.get(token);

  if (entry != null) {
    return history.set(
      token,
      pushUndoStack(
        entry,
        {
          ...entry.last,
          ...next,
        },
        action
      )
    );
  }

  return history;
}

export const selectedTokenReducer = createReducer(
  initialState.selectedToken,
  on(ChartActions.selectChart, (state, action) => action.token),
  on(ChartActions.destroyChartSuccess, (state, action) => (state === action.payload ? null : state))
);

export const selectedFieldToFormatReducer = createReducer(
  initialState.selectedFieldToFormat,
  on(ChartSwitchActions.viewCalcFieldSetFieldToFormat, (state, action) =>
    action.field ? FieldFormatHelpers.getFormatMapKeyByField(action.field) : null
  ),
  on(ChartSwitchActions.viewCalcFieldAllSetFieldToFormat, (state, action) =>
    action.field ? FieldFormatHelpers.getFormatMapKeyByField(action.field) : null
  ),
  on(ChartSwitchActions.vizSettingsComponentRemoveSelectedFieldToFormat, (state, action) => null),
  on(ChartSwitchActions.sharedAxisEncodingSetGroupToFormat, (state, action) =>
    action.group ? FieldFormatHelpers.getFormatMapKeyByGroup(action.group) : null
  ),
  on(ChartActions.selectChart, (state, action) => null)
);

export function tokensReducer(state = initialState.tokens, action: Actions): ChartFeatureState['tokens'] {
  switch (action.type) {
    case ChartActions.loadChartsFromSwitchViewSuccess.type:
      return state.merge(action.charts.map((c) => c.token).filter(isPresent));
    case ChartActions.loadChartsSuccess.type:
    case ChartActions.loadReportViewChartSuccess.type: // fallthrough
      return state.merge(action.payload.map((c) => c.token).filter(isPresent));

    case TempActions.loadExploration.type: // fallthrough
      return state.add(FLAMINGO_RESERVED_REFS.Exploration);

    // TODO: maybe handle AddChart and AddChartFailure?
    case ChartActions.createChartSuccess.type:
      return state.add(action.payload.token);
    case ChartActions.destroyChart.type:
      return state.filter((token) => token !== action.payload);
    case ChartActions.destroyChartFailure.type: // fallthrough
    case ChartSwitchActions.deleteChartFromBrowserFailure.type: // fallthrough
    case ChartSwitchActions.deleteChartFromTabsFailure.type:
      return state.add(action.token);
    case TempActions.Type.DestroyQuerySuccess:
      if (action.chartTokens) {
        return state.filterNot((s) => action.chartTokens.has(s));
      }
      break;

    case ChartActions.makeCopy.type:
    case ChartActions.makeCopyTemp.type:
      return state.add(action.token);

    // Workaround to make sure the modal doesn't pull outdated chart configuration
    case TempActions.Type.ExploreChartWithData:
    case TempActions.Type.ExploreChartMark:
      return state.remove(FLAMINGO_RESERVED_REFS.ChartExplorer);

    case ChartSwitchActions.deleteChartFromBrowser.type:
    case ChartSwitchActions.deleteChartFromTabs.type:
      return state.filter((token) => token !== action.token);

    case ChartSwitchActions.createDefaultChartSuccess.type:
    case ChartSwitchActions.createVegasEncodingChartSuccess.type:
    case ChartSwitchActions.createNewChartSuccess.type:
    case ChartSwitchActions.createNewTableSuccess.type:
    case ChartSwitchActions.cloneChartFromBrowserSuccess.type:
    case ChartSwitchActions.cloneChartFromTabSuccess.type:
    case ChartSwitchActions.cloneChartFromBuilderSuccess.type:
    case ChartSwitchActions.cloneChartFromDesignerSuccess.type:
    case ChartSwitchActions.importDatasetWithChartSuccess.type:
      return state.add(action.chart.token);
  }

  return state;
}

export function versionsReducer(state = initialState.versions, action: Actions): ChartFeatureState['versions'] {
  switch (action.type) {
    case ChartActions.loadChartsFromSwitchViewSuccess.type:
      return state.merge(action.charts.map<[string, 1 | 2]>((c) => [c.token, c.version]));
    case ChartActions.loadChartsSuccess.type:
    case ChartActions.loadReportViewChartSuccess.type: // fallthrough
      return state.merge(action.payload.map<[string, 1 | 2]>((c) => [c.token, c.view_version]));

    case TempActions.loadExploration.type: // fallthrough
      return state.set(FLAMINGO_RESERVED_REFS.Exploration, action.explorationViz.version);

    // TODO: maybe handle AddChart and AddChartFailure?
    case ChartActions.createChartSuccess.type:
      return state.set(action.payload.token, action.payload.view_version);
    case ChartActions.destroyChartSuccess.type:
      return state.remove(action.payload);

    case TempActions.Type.DestroyQuerySuccess:
      return state.removeAll(action.chartTokens);

    case ChartActions.makeCopy.type:
    case ChartActions.makeCopyTemp.type:
      return state.set(action.token, state.get(action.originalToken, 2));

    case ChartSwitchActions.deleteChartFromBrowserSuccess.type:
    case ChartSwitchActions.deleteChartFromTabsSuccess.type:
      return state.remove(action.token);

    case ChartSwitchActions.createDefaultChartSuccess.type:
    case ChartSwitchActions.createVegasEncodingChartSuccess.type:
    case ChartSwitchActions.createNewChartSuccess.type:
    case ChartSwitchActions.createNewTableSuccess.type:
    case ChartSwitchActions.cloneChartFromBrowserSuccess.type:
    case ChartSwitchActions.cloneChartFromTabSuccess.type:
    case ChartSwitchActions.cloneChartFromBuilderSuccess.type:
    case ChartSwitchActions.cloneChartFromDesignerSuccess.type:
    case ChartSwitchActions.importDatasetWithChartSuccess.type:
      return state.set(action.chart.token, action.chart.version);
  }

  return state;
}

export function queryTokensReducer(
  state = initialState.queryTokens,
  action: Actions
): ChartFeatureState['queryTokens'] {
  switch (action.type) {
    case ChartActions.loadChartsFromSwitchViewSuccess.type:
      return state.merge(action.charts.map<[string, string]>((c) => [c.token, c.queryToken]));
    case ChartActions.loadChartsSuccess.type:
    case ChartActions.loadReportViewChartSuccess.type: // fallthrough
      return state.merge(action.payload.map<[string, string]>((c) => [c.token, action.queryToken]));

    case TempActions.loadExploration.type: // fallthrough
      return state.set(FLAMINGO_RESERVED_REFS.Exploration, action.explorationViz.queryToken);

    // TODO: maybe handle AddChart and AddChartFailure?
    case ChartActions.createChartSuccess.type:
      return state.set(action.payload.token, action.queryToken);
    case ChartSwitchActions.createDefaultChartSuccess.type:
    case ChartSwitchActions.createVegasEncodingChartSuccess.type:
    case ChartSwitchActions.createNewChartSuccess.type:
    case ChartSwitchActions.createNewTableSuccess.type:
    case ChartSwitchActions.cloneChartFromBrowserSuccess.type:
    case ChartSwitchActions.cloneChartFromTabSuccess.type:
    case ChartSwitchActions.cloneChartFromBuilderSuccess.type:
    case ChartSwitchActions.cloneChartFromDesignerSuccess.type:
    case ChartSwitchActions.importDatasetWithChartSuccess.type:
      return state.set(action.chart.token, action.queryToken);
    case ChartActions.destroyChartSuccess.type:
      return state.remove(action.payload);
    case TempActions.Type.DestroyQuerySuccess:
      return state.removeAll(action.chartTokens);

    case ChartActions.makeCopy.type:
    case ChartActions.makeCopyTemp.type:
      return state.set(action.token, state.get(action.originalToken, 'MISSING'));
  }

  return state;
}

export function switchViewTokensReducer(
  state = initialState.queryTokens,
  action: Actions
): ChartFeatureState['switchViewTokens'] {
  switch (action.type) {
    case ChartActions.loadChartsFromSwitchViewSuccess.type:
      return state.merge(action.charts.map<[string, string]>((c) => [c.token, c.switchViewToken]));
    case ChartActions.loadChartsSuccess.type:
    case ChartActions.loadReportViewChartSuccess.type: // fallthrough
      return state.merge(action.payload.map<[string, string]>((c) => [c.token, c.switch_view_token]));

    case TempActions.loadExploration.type: // fallthrough
      return state.set(FLAMINGO_RESERVED_REFS.Exploration, action.explorationViz.switchViewToken);

    // TODO: maybe handle AddChart and AddChartFailure?
    case ChartActions.createChartSuccess.type:
      return state.set(action.payload.token, action.payload.switch_view_token);
    case ChartActions.destroyChartSuccess.type:
      return state.remove(action.payload);
    case TempActions.Type.DestroyQuerySuccess:
      return state.removeAll(action.chartTokens);

    case ChartActions.makeCopy.type:
    case ChartActions.makeCopyTemp.type:
      return state.set(action.token, state.get(action.originalToken, 'MISSING'));

    /**
     * This state should be deprecated in favor of the version in the switch-view module,
     * but maintaining for compatibility.
     */
    case ChartSwitchActions.deleteChartFromBrowserSuccess.type:
    case ChartSwitchActions.deleteChartFromTabsSuccess.type:
      return state.remove(action.token);

    case ChartSwitchActions.createDefaultChartSuccess.type:
    case ChartSwitchActions.createVegasEncodingChartSuccess.type:
    case ChartSwitchActions.createNewChartSuccess.type:
    case ChartSwitchActions.createNewTableSuccess.type:
    case ChartSwitchActions.cloneChartFromBrowserSuccess.type:
    case ChartSwitchActions.cloneChartFromTabSuccess.type:
    case ChartSwitchActions.cloneChartFromBuilderSuccess.type:
    case ChartSwitchActions.cloneChartFromDesignerSuccess.type:
    case ChartSwitchActions.importDatasetWithChartSuccess.type:
      return state.set(action.chart.token, action.switchViewToken);
  }

  return state;
}

export function resourcesReducer(state = initialState.resources, action: Actions): ChartFeatureState['resources'] {
  switch (action.type) {
    case ChartActions.loadChartsSuccess.type:
    case ChartActions.loadReportViewChartSuccess.type: // fallthrough
      return state.merge(action.payload.map<[string, ChartTypes.ChartResource]>((c) => [c.token, c]));

    case TempActions.loadExploration.type: // fallthrough
      // Persistence is determined by the presence of a reduced resource, so we must only reduce the resource when
      // persistence is intended, and we must clear any resource on the special key otherwise.
      if (action.withPersistence) {
        return state.set(FLAMINGO_RESERVED_REFS.Exploration, action.explorationViz.resource);
      } else {
        return state.remove(FLAMINGO_RESERVED_REFS.Exploration);
      }
    case TempActions.Type.ClearExplorationResource:
      return state.remove(FLAMINGO_RESERVED_REFS.Exploration);

    case ChartActions.updateChartSuccess.type:
      return state.set(action.resource.token, action.resource);

    case ChartActions.makeCopy.type: {
      const resource = state.get(action.originalToken);
      if (resource != null) {
        return state.set(action.token, resource);
      }

      // An empty resource will certainly result in undefined behavior
      throw new Error(`No resource found for ${action.originalToken}`);
    }
    // TODO: maybe handle AddChart and AddChartFailure?
    case ChartActions.createChartSuccess.type:
      return state.set(action.payload.token, action.payload);
    case ChartActions.destroyChartSuccess.type:
      return state.remove(action.payload);
    case TempActions.Type.DestroyQuerySuccess:
      return state.removeAll(action.chartTokens);
  }

  return state;
}

export function createdTimestampsReducer(
  state = initialState.createdTimestamps,
  action: Actions
): ChartFeatureState['createdTimestamps'] {
  switch (action.type) {
    case ChartActions.loadChartsFromSwitchViewSuccess.type:
      return state.merge(action.charts.map<[string, string]>((c) => [c.token, c.createdAt]));
    case ChartActions.loadChartsSuccess.type:
    case ChartActions.loadReportViewChartSuccess.type: // fallthrough
      return state.merge(action.payload.map<[string, string]>((c) => [c.token, c.created_at]));

    // TODO: maybe handle AddChart and AddChartFailure?
    case ChartActions.createChartSuccess.type:
      return state.set(action.payload.token, action.payload.created_at);
    case ChartActions.destroyChartSuccess.type:
      return state.remove(action.payload);
    case TempActions.Type.DestroyQuerySuccess:
      return state.removeAll(action.chartTokens);

    case ChartActions.makeCopy.type:
    case ChartActions.makeCopyTemp.type:
      return state.set(action.token, state.get(action.originalToken, 'MISSING'));

    /**
     * This state should be deprecated in favor of the version in the switch-view module,
     * but maintaining for compatibility.
     */
    case ChartSwitchActions.deleteChartFromBrowserSuccess.type:
    case ChartSwitchActions.deleteChartFromTabsSuccess.type:
      return state.remove(action.token);

    case ChartSwitchActions.createDefaultChartSuccess.type:
    case ChartSwitchActions.createVegasEncodingChartSuccess.type:
    case ChartSwitchActions.createNewChartSuccess.type:
    case ChartSwitchActions.createNewTableSuccess.type:
    case ChartSwitchActions.cloneChartFromBrowserSuccess.type:
    case ChartSwitchActions.cloneChartFromTabSuccess.type:
    case ChartSwitchActions.cloneChartFromBuilderSuccess.type:
    case ChartSwitchActions.cloneChartFromDesignerSuccess.type:
    case ChartSwitchActions.importDatasetWithChartSuccess.type:
      return state.set(action.chart.token, action.chart.createdAt);
  }

  return state;
}

export function titlesReducer(state = initialState.titles, action: Actions): ChartFeatureState['titles'] {
  switch (action.type) {
    case ChartActions.loadChartsFromSwitchViewSuccess.type:
      return state.merge(action.charts.map<[string, string]>((c) => [c.token, c.title || '']));
    case ChartActions.loadChartsSuccess.type:
    case ChartActions.loadReportViewChartSuccess.type: // fallthrough
      return state.merge(
        action.payload.map<[string, string]>((c) => [c.token, c.view_vegas ? c.view_vegas.title : ''])
      );

    case TempActions.loadExploration.type: // fallthrough
      return state.set(FLAMINGO_RESERVED_REFS.Exploration, action.explorationViz.resource.view_vegas.title);

    // TODO: maybe handle AddChart and AddChartFailure?
    case ChartActions.createChartSuccess.type:
      return state.set(
        action.payload.token,
        action.payload.view_vegas && action.payload.view_vegas ? action.payload.view_vegas.title : ''
      );
    case ChartActions.destroyChartSuccess.type:
      return state.remove(action.payload);
    case TempActions.Type.DestroyQuerySuccess:
      return state.removeAll(action.chartTokens);

    case ChartActions.updateChart.type:
    case ChartActions.updateChartTemp.type:
    case ChartActions.updateChartNoEncoding.type:
    case ChartActions.updateChartNoEncodingTemp.type:
    case ChartSwitchActions.updateChart.type:
    case ChartSwitchActions.updateChartNoEncoding.type:
      return state.set(action.token, action.chart.title);

    case ChartSwitchActions.replaceFromVizStorage.type:
      return state.set(action.token, action.snapshot.title);

    case ChartActions.undo.type: // fallthrough
    case ChartActions.undoTemp.type: // fallthrough
    case ChartActions.redo.type: // fallthrough
    case ChartActions.redoTemp.type:
    case ChartSwitchActions.undo.type:
    case ChartSwitchActions.redo.type:
    case ChartSwitchActions.undoTemp.type:
    case ChartSwitchActions.redoTemp.type:
      if (action.snapshot != null) {
        return state.set(action.token, action.snapshot.title);
      }
      break;

    case ChartActions.makeCopy.type:
    case ChartActions.makeCopyTemp.type:
      return state.set(action.token, action.chart?.title || state.get(action.originalToken));

    case ChartSwitchActions.deleteChartFromBrowserSuccess.type:
    case ChartSwitchActions.deleteChartFromTabsSuccess.type:
      return state.remove(action.token);

    case ChartSwitchActions.createDefaultChartSuccess.type:
    case ChartSwitchActions.createVegasEncodingChartSuccess.type:
    case ChartSwitchActions.createNewChartSuccess.type:
    case ChartSwitchActions.createNewTableSuccess.type:
    case ChartSwitchActions.cloneChartFromBrowserSuccess.type:
    case ChartSwitchActions.cloneChartFromTabSuccess.type:
    case ChartSwitchActions.cloneChartFromBuilderSuccess.type:
    case ChartSwitchActions.cloneChartFromDesignerSuccess.type:
    case ChartSwitchActions.importDatasetWithChartSuccess.type:
      return state.set(action.chart.token, action.chart.title);
  }

  return state;
}

export function descriptionsReducer(
  state = initialState.descriptions,
  action: Actions
): ChartFeatureState['descriptions'] {
  switch (action.type) {
    case ChartActions.loadChartsFromSwitchViewSuccess.type:
      return state.merge(action.charts.map<[string, string]>((c) => [c.token, c.description || '']));
    case ChartActions.loadChartsSuccess.type:
    case ChartActions.loadReportViewChartSuccess.type: // fallthrough
      return state.merge(
        action.payload.map<[string, string]>((c) => [c.token, c.view_vegas ? (c.view_vegas.description ?? '') : ''])
      );

    case TempActions.loadExploration.type: // fallthrough
      return state.set(FLAMINGO_RESERVED_REFS.Exploration, action.explorationViz.resource.view_vegas.description ?? '');

    // TODO: maybe handle AddChart and AddChartFailure?
    case ChartActions.createChartSuccess.type:
      return state.set(
        action.payload.token,
        action.payload.view_vegas && action.payload.view_vegas ? (action.payload.view_vegas.description ?? '') : ''
      );
    case ChartActions.destroyChartSuccess.type:
      return state.remove(action.payload);
    case TempActions.Type.DestroyQuerySuccess:
      return state.removeAll(action.chartTokens);

    case ChartActions.updateChart.type:
    case ChartActions.updateChartTemp.type:
    case ChartActions.updateChartNoEncoding.type:
    case ChartActions.updateChartNoEncodingTemp.type:
    case ChartSwitchActions.updateChart.type:
    case ChartSwitchActions.updateChartNoEncoding.type:
      return state.set(action.token, action.chart.description);

    case ChartSwitchActions.replaceFromVizStorage.type:
      return state.set(action.token, action.snapshot.description);

    case ChartActions.undo.type: // fallthrough
    case ChartActions.undoTemp.type: // fallthrough
    case ChartActions.redo.type: // fallthrough
    case ChartActions.redoTemp.type:
    case ChartSwitchActions.undo.type:
    case ChartSwitchActions.redo.type:
    case ChartSwitchActions.undoTemp.type:
    case ChartSwitchActions.redoTemp.type:
      if (action.snapshot != null) {
        return state.set(action.token, action.snapshot.description);
      }
      break;

    case ChartActions.makeCopy.type:
    case ChartActions.makeCopyTemp.type:
      return state.set(action.token, action.chart?.description || state.get(action.originalToken));

    case ChartSwitchActions.deleteChartFromBrowserSuccess.type:
    case ChartSwitchActions.deleteChartFromTabsSuccess.type:
      return state.remove(action.token);

    case ChartSwitchActions.createDefaultChartSuccess.type:
    case ChartSwitchActions.createVegasEncodingChartSuccess.type:
    case ChartSwitchActions.createNewChartSuccess.type:
    case ChartSwitchActions.createNewTableSuccess.type:
    case ChartSwitchActions.cloneChartFromBrowserSuccess.type:
    case ChartSwitchActions.cloneChartFromTabSuccess.type:
    case ChartSwitchActions.cloneChartFromBuilderSuccess.type:
    case ChartSwitchActions.cloneChartFromDesignerSuccess.type:
    case ChartSwitchActions.importDatasetWithChartSuccess.type:
      return state.set(action.chart.token, action.chart.description);
  }

  return state;
}

export function captionsReducer(state = initialState.captions, action: Actions): ChartFeatureState['captions'] {
  switch (action.type) {
    case ChartActions.loadChartsFromSwitchViewSuccess.type:
      return state.merge(
        action.charts.map<[string, ChartTypes.CaptionInfo]>((c) => [c.token, c.caption || ChartTypes.EMPTY_CAPTION])
      );
    case ChartActions.loadChartsSuccess.type:
      return state.merge(
        action.payload.map<[string, ChartTypes.CaptionInfo]>((c) => [
          c.token,
          c.view_vegas ? (c.view_vegas.caption ?? ChartTypes.EMPTY_CAPTION) : ChartTypes.EMPTY_CAPTION,
        ])
      );

    case TempActions.loadExploration.type: // fallthrough
      return state.set(
        FLAMINGO_RESERVED_REFS.Exploration,
        action.explorationViz.resource.view_vegas.caption ?? ChartTypes.EMPTY_CAPTION
      );

    // TODO: maybe handle AddChart and AddChartFailure?
    case ChartActions.createChartSuccess.type:
      return state.set(
        action.payload.token,
        action.payload.view_vegas && action.payload.view_vegas
          ? (action.payload.view_vegas.caption ?? ChartTypes.EMPTY_CAPTION)
          : ChartTypes.EMPTY_CAPTION
      );
    case ChartActions.destroyChartSuccess.type:
      return state.remove(action.payload);
    case TempActions.Type.DestroyQuerySuccess:
      return state.removeAll(action.chartTokens);

    case ChartActions.updateChart.type:
    case ChartActions.updateChartTemp.type:
    case ChartActions.updateChartNoEncoding.type:
    case ChartActions.updateChartNoEncodingTemp.type:
    case ChartSwitchActions.updateChart.type:
    case ChartSwitchActions.updateChartNoEncoding.type:
      return state.set(action.token, action.chart.caption);

    case ChartSwitchActions.replaceFromVizStorage.type:
      return state.set(action.token, action.snapshot.caption);

    case ChartActions.undo.type: // fallthrough
    case ChartActions.undoTemp.type: // fallthrough
    case ChartActions.redo.type: // fallthrough
    case ChartActions.redoTemp.type:
    case ChartSwitchActions.undo.type:
    case ChartSwitchActions.redo.type:
    case ChartSwitchActions.undoTemp.type:
    case ChartSwitchActions.redoTemp.type:
      if (action.snapshot != null) {
        return state.set(action.token, action.snapshot.caption);
      }
      break;

    case ChartActions.makeCopy.type:
    case ChartActions.makeCopyTemp.type:
      return state.set(
        action.token,
        action.chart?.caption || state.get(action.originalToken) || ChartTypes.EMPTY_CAPTION
      );

    case ChartSwitchActions.deleteChartFromBrowserSuccess.type:
    case ChartSwitchActions.deleteChartFromTabsSuccess.type:
      return state.remove(action.token);

    case ChartSwitchActions.createDefaultChartSuccess.type:
    case ChartSwitchActions.createVegasEncodingChartSuccess.type:
    case ChartSwitchActions.createNewChartSuccess.type:
    case ChartSwitchActions.createNewTableSuccess.type:
    case ChartSwitchActions.cloneChartFromBrowserSuccess.type:
    case ChartSwitchActions.cloneChartFromTabSuccess.type:
    case ChartSwitchActions.cloneChartFromBuilderSuccess.type:
    case ChartSwitchActions.cloneChartFromDesignerSuccess.type:
    case ChartSwitchActions.importDatasetWithChartSuccess.type:
      return state.set(action.chart.token, action.chart.caption || ChartTypes.EMPTY_CAPTION);

    case ChartActions.updateCaption.type:
    case ChartSwitchActions.updateCaption.type:
      return state.set(action.token, action.caption);
  }

  return state;
}

export function chartTypesReducer(state = initialState.chartTypes, action: Actions): ChartFeatureState['chartTypes'] {
  switch (action.type) {
    case ChartActions.loadChartsFromSwitchViewSuccess.type:
      return state.merge(action.charts.map<[string, ChartTypes.ChartType]>((c) => [c.token, c.chartType]));
    case ChartActions.loadChartsSuccess.type:
    case ChartActions.loadReportViewChartSuccess.type: // fallthrough
      return state.merge(
        action.payload.map<[string, ChartTypes.ChartType]>((c) => [
          c.token,
          c.view_vegas ? c.view_vegas.chartType : 'line',
        ])
      );

    case TempActions.loadExploration.type: // fallthrough
      return state.set(FLAMINGO_RESERVED_REFS.Exploration, action.explorationViz.chartType);

    // TODO: maybe handle AddChart and AddChartFailure?
    case ChartActions.createChartSuccess.type:
      return state.set(action.payload.token, action.payload.view_vegas ? action.payload.view_vegas.chartType : 'line');
    case ChartActions.destroyChartSuccess.type:
      return state.remove(action.payload);
    case TempActions.Type.DestroyQuerySuccess:
      return state.removeAll(action.chartTokens);

    case ChartActions.updateChart.type:
    case ChartActions.updateChartTemp.type:
    case ChartSwitchActions.updateChart.type:
      return state.set(action.token, action.chart.chartType);

    case ChartSwitchActions.replaceFromVizStorage.type:
      return state.set(action.token, action.snapshot.chartType);

    case ChartActions.undo.type: // fallthrough
    case ChartActions.undoTemp.type: // fallthrough
    case ChartActions.redo.type: // fallthrough
    case ChartActions.redoTemp.type:
    case ChartSwitchActions.undo.type:
    case ChartSwitchActions.redo.type:
    case ChartSwitchActions.undoTemp.type:
    case ChartSwitchActions.redoTemp.type:
      if (action.snapshot) {
        return state.set(action.token, action.snapshot.chartType);
      }
      break;

    case ChartActions.makeCopy.type:
    case ChartActions.makeCopyTemp.type:
      return state.set(action.token, action.chart?.chartType || state.get(action.originalToken, 'table'));

    case ChartSwitchActions.deleteChartFromBrowserSuccess.type:
    case ChartSwitchActions.deleteChartFromTabsSuccess.type:
      return state.remove(action.token);

    case ChartSwitchActions.createDefaultChartSuccess.type:
    case ChartSwitchActions.createVegasEncodingChartSuccess.type:
    case ChartSwitchActions.createNewChartSuccess.type:
    case ChartSwitchActions.createNewTableSuccess.type:
    case ChartSwitchActions.cloneChartFromBrowserSuccess.type:
    case ChartSwitchActions.cloneChartFromTabSuccess.type:
    case ChartSwitchActions.cloneChartFromBuilderSuccess.type:
    case ChartSwitchActions.cloneChartFromDesignerSuccess.type:
    case ChartSwitchActions.importDatasetWithChartSuccess.type:
      return state.set(action.chart.token, action.chart.chartType);
  }

  return state;
}

export function encodingsReducer(state = initialState.encodings, action: Actions): ChartFeatureState['encodings'] {
  switch (action.type) {
    case ChartActions.loadChartsFromSwitchViewSuccess.type:
      return state.merge(
        action.charts.map<[string, Encoding]>((c) => {
          if (c.version === 2) {
            return [c.token, EncodingHelpers.normalizeEncoding(c.encoding)];
          } else {
            return [c.token, EMPTY_ENCODING];
          }
        })
      );
    case ChartActions.loadChartsSuccess.type:
    case ChartActions.loadReportViewChartSuccess.type: // fallthrough
      return state.merge(
        action.payload.map<[string, Encoding]>((c) => {
          if (c.view_version === 2) {
            return [
              c.token,
              c.view_vegas && c.view_vegas.encoding
                ? EncodingHelpers.normalizeEncoding(c.view_vegas.encoding)
                : EncodingHelpers.initEncoding(c.view_vegas.chartType).encoding,
            ];
          } else {
            return [c.token, EMPTY_ENCODING];
          }
        })
      );

    case TempActions.loadExploration.type: // fallthrough
      return state.set(
        FLAMINGO_RESERVED_REFS.Exploration,
        EncodingHelpers.normalizeEncoding(action.explorationViz.encoding)
      );

    // TODO: maybe handle AddChart and AddChartFailure?
    case ChartActions.createChartSuccess.type:
      if (action.payload.view_version === 2) {
        if (!action.payload.view_vegas.encoding) {
          const chartType = action.payload.view_vegas.chartType || 'line';
          return state.set(action.payload.token, EncodingHelpers.initEncoding(chartType).encoding);
        } else {
          return state.set(action.payload.token, action.payload.view_vegas.encoding);
        }
      } else {
        return state.set(action.payload.token, EMPTY_ENCODING);
      }
    case ChartActions.destroyChartSuccess.type:
      return state.remove(action.payload);
    case TempActions.Type.DestroyQuerySuccess:
      return state.removeAll(action.chartTokens);

    case ChartActions.updateChart.type:
    case ChartActions.updateChartTemp.type:
    case ChartSwitchActions.updateChart.type:
      return state.set(action.token, EncodingHelpers.normalizeEncoding(action.chart.encoding));

    case ChartSwitchActions.replaceFromVizStorage.type:
      return state.set(action.token, EncodingHelpers.normalizeEncoding(action.snapshot.encoding));

    case ChartActions.undo.type: // fallthrough
    case ChartActions.undoTemp.type: // fallthrough
    case ChartActions.redo.type: // fallthrough
    case ChartActions.redoTemp.type:
    case ChartSwitchActions.undo.type:
    case ChartSwitchActions.redo.type:
    case ChartSwitchActions.undoTemp.type:
    case ChartSwitchActions.redoTemp.type:
      if (action.snapshot) {
        return state.set(action.token, EncodingHelpers.normalizeEncoding(action.snapshot.encoding));
      }
      break;

    case ChartActions.makeCopy.type:
    case ChartActions.makeCopyTemp.type:
      return state.set(
        action.token,
        EncodingHelpers.normalizeEncoding(action.chart?.encoding || state.get(action.originalToken, EMPTY_ENCODING))
      );

    case ChartSwitchActions.deleteChartFromBrowserSuccess.type:
    case ChartSwitchActions.deleteChartFromTabsSuccess.type:
      return state.remove(action.token);

    case ChartSwitchActions.createDefaultChartSuccess.type:
    case ChartSwitchActions.createVegasEncodingChartSuccess.type:
    case ChartSwitchActions.createNewChartSuccess.type:
    case ChartSwitchActions.createNewTableSuccess.type:
    case ChartSwitchActions.cloneChartFromBrowserSuccess.type:
    case ChartSwitchActions.cloneChartFromTabSuccess.type:
    case ChartSwitchActions.cloneChartFromBuilderSuccess.type:
    case ChartSwitchActions.cloneChartFromDesignerSuccess.type:
    case ChartSwitchActions.importDatasetWithChartSuccess.type:
      return state.set(action.chart.token, EncodingHelpers.normalizeEncoding(action.chart.encoding));
  }

  return state;
}

export function formatsReducer(state = initialState.formats, action: Actions): ChartFeatureState['formats'] {
  switch (action.type) {
    case ChartActions.loadChartsFromSwitchViewSuccess.type:
      return state.merge(action.charts.map<[string, ChartFormat]>((c) => [c.token, loadChartFormatFromMappedChart(c)]));
    case ChartActions.loadChartsSuccess.type:
    case ChartActions.loadReportViewChartSuccess.type: // fallthrough
      return state.merge(action.payload.map<[string, ChartFormat]>((c) => [c.token, loadChartFormat(c)]));

    case TempActions.loadExploration.type: // fallthrough
      return state.set(FLAMINGO_RESERVED_REFS.Exploration, action.explorationViz.format);

    // TODO: maybe handle AddChart and AddChartFailure?
    case ChartActions.createChartSuccess.type: {
      return state.set(action.payload.token, action.payload.view_vegas.format);
    }
    case ChartActions.destroyChartSuccess.type:
      return state.remove(action.payload);
    case TempActions.Type.DestroyQuerySuccess:
      return state.removeAll(action.chartTokens);

    case ChartActions.undo.type: // fallthrough
    case ChartActions.undoTemp.type: // fallthrough
    case ChartActions.redo.type: // fallthrough
    case ChartActions.redoTemp.type:
    case ChartSwitchActions.undo.type:
    case ChartSwitchActions.redo.type:
    case ChartSwitchActions.undoTemp.type:
    case ChartSwitchActions.redoTemp.type:
      if (action.snapshot) {
        return state.set(action.token, action.snapshot.format);
      }
      break;

    case ChartActions.updateChart.type:
    case ChartActions.updateChartTemp.type:
    case ChartActions.updateChartNoEncoding.type:
    case ChartActions.updateChartNoEncodingTemp.type:
    case ChartSwitchActions.updateChart.type:
    case ChartSwitchActions.updateChartNoEncoding.type:
      return state.set(action.token, action.chart.format);

    case ChartSwitchActions.replaceFromVizStorage.type:
      return state.set(action.token, action.snapshot.format);

    case ChartActions.makeCopy.type:
    case ChartActions.makeCopyTemp.type:
      return state.set(action.token, action.chart?.format || state.get(action.originalToken, DEFAULT_CHART_FORMAT));

    case ChartSwitchActions.deleteChartFromBrowserSuccess.type:
    case ChartSwitchActions.deleteChartFromTabsSuccess.type:
      return state.remove(action.token);

    case ChartSwitchActions.createDefaultChartSuccess.type:
    case ChartSwitchActions.createVegasEncodingChartSuccess.type:
    case ChartSwitchActions.createNewChartSuccess.type:
    case ChartSwitchActions.createNewTableSuccess.type:
    case ChartSwitchActions.cloneChartFromBrowserSuccess.type:
    case ChartSwitchActions.cloneChartFromTabSuccess.type:
    case ChartSwitchActions.cloneChartFromBuilderSuccess.type:
    case ChartSwitchActions.cloneChartFromDesignerSuccess.type:
    case ChartSwitchActions.importDatasetWithChartSuccess.type:
      return state.set(action.chart.token, action.chart.format);
  }

  return state;
}

export function fieldFormatsReducer(
  state = initialState.fieldFormats,
  action: Actions
): ChartFeatureState['fieldFormats'] {
  switch (action.type) {
    /**
     * TODO:
     * On each encoding update, enumerate all formats and if any are no longer in the encoding, then remove them
     * Add side-effects to persist
     */
    case ChartActions.loadChartsFromSwitchViewSuccess.type:
      return state.merge(
        action.charts.map<[string, ChartTypes.FieldFormatMap]>((c) => [c.token, c.fieldFormats ?? {}])
      );
    case ChartActions.loadChartsSuccess.type:
    case ChartActions.loadReportViewChartSuccess.type: // fallthrough
      return state.merge(
        action.payload.map<[string, ChartTypes.FieldFormatMap]>((c) => [c.token, c.view_vegas.fieldFormats ?? {}])
      );

    case TempActions.loadExploration.type: // fallthrough
      return state.set(FLAMINGO_RESERVED_REFS.Exploration, action.explorationViz.fieldFormats ?? {});

    case ChartActions.createChartSuccess.type:
      return state.set(action.payload.token, action.payload.view_vegas.fieldFormats ?? {});

    case ChartActions.undo.type: // fallthrough
    case ChartActions.undoTemp.type: // fallthrough
    case ChartActions.redo.type: // fallthrough
    case ChartActions.redoTemp.type:
    case ChartSwitchActions.undo.type:
    case ChartSwitchActions.redo.type:
    case ChartSwitchActions.undoTemp.type:
    case ChartSwitchActions.redoTemp.type:
      if (action.snapshot) {
        return state.set(action.token, action.snapshot.fieldFormats ?? {});
      }
      break;

    case ChartActions.updateChart.type:
    case ChartActions.updateChartTemp.type:
    case ChartActions.updateChartNoEncoding.type:
    case ChartActions.updateChartNoEncodingTemp.type:
    case ChartSwitchActions.updateChart.type:
    case ChartSwitchActions.updateChartNoEncoding.type:
      return state.set(action.token, action.chart.fieldFormats);

    case ChartSwitchActions.replaceFromVizStorage.type:
      return state.set(action.token, action.snapshot.fieldFormats);

    case ChartActions.makeCopy.type:
    case ChartActions.makeCopyTemp.type:
      return state.set(action.token, action.chart?.fieldFormats || state.get(action.originalToken, {}));

    // TODO: Delete chart/query cleanups

    case ChartSwitchActions.deleteChartFromBrowserSuccess.type:
    case ChartSwitchActions.deleteChartFromTabsSuccess.type:
      return state.remove(action.token);

    case ChartSwitchActions.createDefaultChartSuccess.type:
    case ChartSwitchActions.createVegasEncodingChartSuccess.type:
    case ChartSwitchActions.createNewChartSuccess.type:
    case ChartSwitchActions.createNewTableSuccess.type:
    case ChartSwitchActions.cloneChartFromBrowserSuccess.type:
    case ChartSwitchActions.cloneChartFromTabSuccess.type:
    case ChartSwitchActions.cloneChartFromBuilderSuccess.type:
    case ChartSwitchActions.cloneChartFromDesignerSuccess.type:
    case ChartSwitchActions.importDatasetWithChartSuccess.type:
      return state.set(action.chart.token, action.chart.fieldFormats);
  }
  return state;
}

export function fieldColorsReducer(
  state = initialState.fieldColors,
  action: Actions
): ChartFeatureState['fieldColors'] {
  switch (action.type) {
    /**
     * TODO:
     * On each encoding update, enumerate all formats and if any are no longer in the encoding, then remove them
     * Add side-effects to persist
     */
    case ChartActions.loadChartsFromSwitchViewSuccess.type:
      return state.merge(action.charts.map<[string, ChartTypes.FieldColorMap]>((c) => [c.token, c.fieldColors]));
    case ChartActions.loadChartsSuccess.type:
    case ChartActions.loadReportViewChartSuccess.type: // fallthrough
      return state.merge(
        action.payload.map<[string, ChartTypes.FieldColorMap]>((c) => [c.token, c.view_vegas.fieldColors])
      );

    case TempActions.loadExploration.type: // fallthrough
      return state.set(FLAMINGO_RESERVED_REFS.Exploration, action.explorationViz.fieldColors);

    case ChartActions.createChartSuccess.type:
      return state.set(action.payload.token, action.payload.view_vegas.fieldColors);

    case ChartActions.undo.type: // fallthrough
    case ChartActions.undoTemp.type: // fallthrough
    case ChartActions.redo.type: // fallthrough
    case ChartActions.redoTemp.type:
    case ChartSwitchActions.undo.type:
    case ChartSwitchActions.redo.type:
      if (action.snapshot) {
        return state.set(action.token, action.snapshot.fieldColors);
      }
      break;

    case ChartActions.updateChart.type:
    case ChartActions.updateChartTemp.type:
    case ChartActions.updateChartNoEncoding.type:
    case ChartActions.updateChartNoEncodingTemp.type:
    case ChartSwitchActions.updateChart.type:
    case ChartSwitchActions.updateChartNoEncoding.type:
      return state.set(action.token, action.chart.fieldColors);

    case ChartSwitchActions.replaceFromVizStorage.type:
      return state.set(action.token, action.snapshot.fieldColors);

    case ChartActions.makeCopy.type:
    case ChartActions.makeCopyTemp.type:
      return state.set(action.token, action.chart?.fieldColors || state.get(action.originalToken, {}));

    // TODO: Delete chart/query cleanups

    case ChartSwitchActions.deleteChartFromBrowserSuccess.type:
    case ChartSwitchActions.deleteChartFromTabsSuccess.type:
      return state.remove(action.token);

    case ChartSwitchActions.createDefaultChartSuccess.type:
    case ChartSwitchActions.createVegasEncodingChartSuccess.type:
    case ChartSwitchActions.createNewChartSuccess.type:
    case ChartSwitchActions.createNewTableSuccess.type:
    case ChartSwitchActions.cloneChartFromBrowserSuccess.type:
    case ChartSwitchActions.cloneChartFromTabSuccess.type:
    case ChartSwitchActions.cloneChartFromBuilderSuccess.type:
    case ChartSwitchActions.cloneChartFromDesignerSuccess.type:
    case ChartSwitchActions.importDatasetWithChartSuccess.type:
      return state.set(action.chart.token, action.chart.fieldColors);
  }
  return state;
}

export function colorPaletteTokensReducer(
  state = initialState.colorPaletteTokens,
  action: Actions
): ChartFeatureState['colorPaletteTokens'] {
  switch (action.type) {
    case ChartActions.loadChartsFromSwitchViewSuccess.type:
      return state.merge(action.charts.map<[string, string]>((c) => [c.token, c.colorPaletteToken]));
    case ChartActions.loadChartsSuccess.type:
    case ChartActions.loadReportViewChartSuccess.type: // fallthrough
      return state.merge(action.payload.map<[string, string]>((c) => [c.token, c.color_palette_token]));

    case TempActions.loadExploration.type: // fallthrough
      return state.set(FLAMINGO_RESERVED_REFS.Exploration, action.explorationViz.colorPaletteToken);

    case ChartActions.updateChartSuccess.type: // For V1 big numbers
      return state.set(action.resource.token, action.resource.color_palette_token);

    case ChartActions.updateChart.type:
    case ChartActions.updateChartTemp.type:
    case ChartActions.updateChartNoEncoding.type:
    case ChartActions.updateChartNoEncodingTemp.type:
    case ChartSwitchActions.updateChart.type:
    case ChartSwitchActions.updateChartNoEncoding.type:
      return state.set(action.token, action.chart.colorPaletteToken);

    case ChartSwitchActions.replaceFromVizStorage.type:
      return state.set(action.token, action.snapshot.colorPaletteToken);

    // TODO: maybe handle AddChart and AddChartFailure?
    case ChartActions.createChartSuccess.type:
      return state.set(action.payload.token, action.payload.color_palette_token);
    case ChartActions.destroyChartSuccess.type:
      return state.remove(action.payload);
    case TempActions.Type.DestroyQuerySuccess:
      return state.removeAll(action.chartTokens);

    case ChartActions.undo.type: // fallthrough
    case ChartActions.undoTemp.type: // fallthrough
    case ChartActions.redo.type: // fallthrough
    case ChartActions.redoTemp.type:
    case ChartSwitchActions.undo.type:
    case ChartSwitchActions.redo.type:
    case ChartSwitchActions.undoTemp.type:
    case ChartSwitchActions.redoTemp.type:
      if (action.snapshot) {
        return state.set(action.token, action.snapshot.colorPaletteToken);
      }
      break;

    case ChartActions.makeCopy.type:
    case ChartActions.makeCopyTemp.type:
      return state.set(action.token, action.chart?.colorPaletteToken || state.get(action.originalToken, ''));

    case ChartSwitchActions.deleteChartFromBrowserSuccess.type:
    case ChartSwitchActions.deleteChartFromTabsSuccess.type:
      return state.remove(action.token);

    case ChartSwitchActions.createDefaultChartSuccess.type:
    case ChartSwitchActions.createVegasEncodingChartSuccess.type:
    case ChartSwitchActions.createNewChartSuccess.type:
    case ChartSwitchActions.createNewTableSuccess.type:
    case ChartSwitchActions.cloneChartFromBrowserSuccess.type:
    case ChartSwitchActions.cloneChartFromTabSuccess.type:
    case ChartSwitchActions.cloneChartFromBuilderSuccess.type:
    case ChartSwitchActions.cloneChartFromDesignerSuccess.type:
    case ChartSwitchActions.importDatasetWithChartSuccess.type:
      return state.set(action.chart.token, action.chart.colorPaletteToken);
  }

  return state;
}

export function facetSizeReducer(state = initialState.facetSizes, action: Actions): ChartFeatureState['facetSizes'] {
  switch (action.type) {
    case ChartActions.loadChartsFromSwitchViewSuccess.type:
      return state.merge(action.charts.map<[string, ChartTypes.FacetSizeMap]>((c) => [c.token, c.facetSizes]));
    case ChartActions.loadChartsSuccess.type:
    case ChartActions.loadReportViewChartSuccess.type: // fallthrough
      return state.merge(
        action.payload.map<[string, ChartTypes.FacetSizeMap]>((c) => [c.token, c.view_vegas.facetSizes])
      );

    case TempActions.loadExploration.type:
      return state.set(FLAMINGO_RESERVED_REFS.Exploration, action.explorationViz.facetSizes);

    case ChartActions.createChartSuccess.type:
      return state.set(action.payload.token, action.payload.view_vegas.facetSizes);

    case ChartActions.undo.type:
    case ChartActions.undoTemp.type:
    case ChartActions.redo.type:
    case ChartActions.redoTemp.type:
    case ChartSwitchActions.undo.type:
    case ChartSwitchActions.redo.type:
      if (action.snapshot) {
        return state.set(action.token, action.snapshot.facetSizes);
      }
      break;

    case ChartActions.updateChart.type:
    case ChartActions.updateChartTemp.type:
    case ChartActions.updateChartNoEncoding.type:
    case ChartActions.updateChartNoEncodingTemp.type:
    case ChartSwitchActions.updateChart.type:
    case ChartSwitchActions.updateChartNoEncoding.type:
      return state.set(action.token, action.chart.facetSizes);

    case ChartSwitchActions.replaceFromVizStorage.type:
      return state.set(action.token, action.snapshot.facetSizes);

    case ChartActions.makeCopy.type:
    case ChartActions.makeCopyTemp.type:
      return state.set(action.token, action.chart?.facetSizes || state.get(action.originalToken, {}));

    case ChartSwitchActions.deleteChartFromBrowserSuccess.type:
    case ChartSwitchActions.deleteChartFromTabsSuccess.type:
      return state.remove(action.token);

    case ChartSwitchActions.createDefaultChartSuccess.type:
    case ChartSwitchActions.createVegasEncodingChartSuccess.type:
    case ChartSwitchActions.createNewChartSuccess.type:
    case ChartSwitchActions.createNewTableSuccess.type:
    case ChartSwitchActions.cloneChartFromBrowserSuccess.type:
    case ChartSwitchActions.cloneChartFromTabSuccess.type:
    case ChartSwitchActions.cloneChartFromBuilderSuccess.type:
    case ChartSwitchActions.cloneChartFromDesignerSuccess.type:
    case ChartSwitchActions.importDatasetWithChartSuccess.type:
      return state.set(action.chart.token, action.chart.facetSizes);
  }
  return state;
}

export function v1ViewsReducer(state = initialState.v1Views, action: Actions): ChartFeatureState['v1Views'] {
  switch (action.type) {
    // case RouterActionType.UpdateRoute:
    //   if (!action.payload.params.reportId) {
    //     return state.clear();
    //   }
    //   break;
    case ChartActions.loadChartsFromSwitchViewSuccess.type:
      return state.merge(action.charts.map<[string, any]>((c) => [c.token, {}]));
    case ChartActions.loadChartsSuccess.type:
    case ChartActions.loadReportViewChartSuccess.type: // fallthrough
      return state.merge(action.payload.map<[string, any]>((c) => [c.token, c.view]));
    case TempActions.Type.DestroyQuerySuccess:
      return state.removeAll(action.chartTokens);
    case ChartActions.updateChartSuccess.type:
      return state.set(action.resource.token, action.resource.view);
    case ChartSwitchActions.createDefaultChartSuccess.type:
    case ChartSwitchActions.createVegasEncodingChartSuccess.type:
    case ChartSwitchActions.createNewChartSuccess.type:
    case ChartSwitchActions.createNewTableSuccess.type:
    case ChartSwitchActions.cloneChartFromBrowserSuccess.type:
    case ChartSwitchActions.cloneChartFromTabSuccess.type:
    case ChartSwitchActions.cloneChartFromBuilderSuccess.type:
    case ChartSwitchActions.cloneChartFromDesignerSuccess.type:
    case ChartSwitchActions.importDatasetWithChartSuccess.type:
      return state.set(action.chart.token, {} as any);
    // TODO: maybe handle AddChart and AddChartFailure?
    case ChartActions.createChartSuccess.type:
      return state.set(action.payload.token, action.payload.view);
    case ChartActions.destroyChartSuccess.type:
      return state.remove(action.payload);
    case ChartActions.updateTitle.type: {
      const view = state.get(action.token);
      if (view != null) {
        return state.set(action.token, {
          ...view,
          chartTitle: action.title,
        });
      }
      break;
    }
    case ChartActions.makeCopy.type:
    case ChartActions.makeCopyTemp.type: {
      const view = state.get(action.originalToken);
      if (view != null) {
        return state.set(action.token, view);
      }

      // If it's a V1 chart, this will be bad, but V2 charts don't need to have this
      console.warn(`Unable to find V1 view for ${action.originalToken}`);
    }
  }

  return state;
}

export function isAutoUpdateDisabledReducer(
  state = initialState.isAutoUpdateDisabled,
  action: Actions
): ChartFeatureState['isAutoUpdateDisabled'] {
  switch (action.type) {
    case ChartActions.loadChartsFromSwitchViewSuccess.type:
      return state.merge(action.charts.map<[string, boolean]>((c) => [c.token, c.isAutoUpdateDisabled]));
    case ChartActions.loadChartsSuccess.type:
      return state.merge(
        action.payload.map<[string, boolean]>((c) => [
          c.token,
          c.view_vegas ? c.view_vegas.isAutoUpdateDisabled : false,
        ])
      );
    case TempActions.loadExploration.type:
      return state.set(FLAMINGO_RESERVED_REFS.Exploration, action.explorationViz.isAutoUpdateDisabled);
    case ChartActions.createChartSuccess.type:
      return state.set(
        action.payload.token,
        action.payload.view_vegas ? action.payload.view_vegas.isAutoUpdateDisabled : false
      );
    case ChartActions.destroyChartSuccess.type:
      return state.remove(action.payload);
    case TempActions.Type.DestroyQuerySuccess:
      return state.removeAll(action.chartTokens);
    case ChartSwitchActions.deleteChartFromBrowserSuccess.type:
    case ChartSwitchActions.deleteChartFromTabsSuccess.type:
      return state.remove(action.token);

    case ChartActions.updateChart.type:
    case ChartActions.updateChartTemp.type:
    case ChartSwitchActions.updateChart.type:
      return state.set(action.token, action.chart.isAutoUpdateDisabled);

    case ChartSwitchActions.replaceFromVizStorage.type:
      return state.set(action.token, false);

    case ChartActions.undo.type:
    case ChartActions.undoTemp.type:
    case ChartActions.redo.type:
    case ChartActions.redoTemp.type:
    case ChartSwitchActions.undo.type:
    case ChartSwitchActions.redo.type:
    case ChartSwitchActions.undoTemp.type:
    case ChartSwitchActions.redoTemp.type:
      if (action.snapshot) {
        return state.set(action.token, action.snapshot.isAutoUpdateDisabled);
      }
      break;

    case ChartActions.makeCopy.type:
    case ChartActions.makeCopyTemp.type:
      return state.set(action.token, action.chart?.isAutoUpdateDisabled || state.get(action.originalToken, false));

    case ChartSwitchActions.createDefaultChartSuccess.type:
    case ChartSwitchActions.createVegasEncodingChartSuccess.type:
    case ChartSwitchActions.createNewChartSuccess.type:
    case ChartSwitchActions.createNewTableSuccess.type:
    case ChartSwitchActions.cloneChartFromBrowserSuccess.type:
    case ChartSwitchActions.cloneChartFromTabSuccess.type:
    case ChartSwitchActions.cloneChartFromBuilderSuccess.type:
    case ChartSwitchActions.cloneChartFromDesignerSuccess.type:
    case ChartSwitchActions.importDatasetWithChartSuccess.type:
      return state.set(action.chart.token, action.chart.isAutoUpdateDisabled);

    case ChartActions.updateIsAutoUpdateDisabled.type:
    case ChartSwitchActions.updateIsAutoUpdateDisabled.type:
      return state.set(action.token, action.isAutoUpdateDisabled);
  }

  return state;
}

export function manuallyApplyChangesReducer(state = initialState.manuallyApplyChanges, action: Actions) {
  switch (action.type) {
    case ChartActions.manuallyApplyChanges.type:
    case ChartSwitchActions.manuallyApplyChanges.type:
      return state.set(action.token, true);
    // Whenever there is a change to the visualization, we automatically turn off manuallyApplyChanges
    // so that the isAutoUpdateDisabled setting is honored. Changes include selecting a different chart tab,
    // modifying an encoding, changing a format or color, undo/redo. This list may need to be extended
    // in the future.
    case ChartActions.selectChart.type:
    case ChartActions.updateChart.type:
    case ChartActions.updateChartNoEncoding.type:
    case ChartSwitchActions.updateChart.type:
    case ChartSwitchActions.updateChartNoEncoding.type:
    case ChartActions.updateType.type:
    case ChartActions.updateEncoding.type:
    case ChartSwitchActions.updateDesignerChartEncoding.type:
    case ChartSwitchActions.updateEncodingAndFormat.type:
    case ChartActions.updateEncodingAndFormat.type:
    case ChartActions.undo.type:
    case ChartSwitchActions.undo.type:
    case ChartActions.redo.type:
    case ChartSwitchActions.redo.type:
    case ChartActions.updateIsAutoUpdateDisabled.type:
    case ChartSwitchActions.updateIsAutoUpdateDisabled.type:
    case ChartSwitchActions.replaceFromVizStorage.type:
      return state.set(action.token, false);
  }
  return state;
}

export function unappliedChangesCountReducer(state = initialState.unappliedChangesCount, action: Actions) {
  switch (action.type) {
    case ChartActions.updateIsAutoUpdateDisabled.type:
    case ChartSwitchActions.updateIsAutoUpdateDisabled.type:
    case ChartActions.manuallyApplyChanges.type:
    case ChartSwitchActions.manuallyApplyChanges.type:
      return state.set(action.token, 0);
    case ChartActions.setUnappliedChangesCount.type:
    case ChartSwitchActions.setUnappliedChangesCount.type:
      return state.set(action.token, action.count);
  }
  return state;
}

export function ephemeralsReducer(state = initialState.ephemerals, action: Actions): typeof state {
  switch (action.type) {
    case ChartSwitchActions.clearEphemeral.type:
      return state.remove(action.token);
    case ChartSwitchActions.setEphemeral.type:
      return state.set(action.token, action.state ?? true);
    case ChartSwitchActions.updateChartSuccess.type:
    case ChartSwitchActions.updateChartFailure.type:
      return state.has(action.token) ? state.set(action.token, true) : state;
    case ChartSwitchActions.updateChartFromSnapshot.type:
      return state.has(action.token) ? state.set(action.token, false) : state;
    case ChartActions.makeCopy.type:
    case ChartActions.makeCopyTemp.type: {
      const initialValue = state.get(action.originalToken);
      if (typeof initialValue === 'boolean') {
        return state.set(action.token, initialValue);
      } else {
        return state;
      }
    }
    default: {
      return state;
    }
  }
}

export function historiesReducer(state = initialState.histories, action: Actions): ChartFeatureState['histories'] {
  switch (action.type) {
    case ChartActions.loadChartsFromSwitchViewSuccess.type:
      return state.merge(
        action.charts.map<[string, ChartTypes.ChartHistory]>((c) => [c.token, makeChartHistoryStackFromMappedChart(c)])
      );
    case ChartActions.loadChartsSuccess.type:
    case ChartActions.loadReportViewChartSuccess.type: // fallthrough
      return state.merge(
        action.payload.map<[string, ChartTypes.ChartHistory]>((c) => [c.token, makeChartHistoryStack(c)])
      );

    case TempActions.loadExploration.type: // fallthrough
      return state.set(FLAMINGO_RESERVED_REFS.Exploration, makeChartHistoryStack(action.explorationViz.resource));

    // TODO: maybe handle AddChart and AddChartFailure?
    case ChartActions.createChartSuccess.type:
      return state.set(action.payload.token, makeChartHistoryStack(action.payload));
    case ChartSwitchActions.createDefaultChartSuccess.type:
    case ChartSwitchActions.createVegasEncodingChartSuccess.type:
    case ChartSwitchActions.createNewChartSuccess.type:
    case ChartSwitchActions.createNewTableSuccess.type:
    case ChartSwitchActions.cloneChartFromBrowserSuccess.type:
    case ChartSwitchActions.cloneChartFromTabSuccess.type:
    case ChartSwitchActions.cloneChartFromBuilderSuccess.type:
    case ChartSwitchActions.cloneChartFromDesignerSuccess.type:
    case ChartSwitchActions.importDatasetWithChartSuccess.type:
      return state.set(action.chart.token, makeChartHistoryStackFromMappedChart(action.chart));
    case ChartActions.destroyChartSuccess.type:
      return state.remove(action.payload);
    case TempActions.Type.DestroyQuerySuccess:
      return state.removeAll(action.chartTokens);

    case ChartActions.updateChart.type:
    case ChartActions.updateChartNoEncoding.type:
      return pushToHistory(state, action.token, action.chart, action.description);
    case ChartSwitchActions.updateChart.type:
    case ChartSwitchActions.updateChartNoEncoding.type:
      return pushToHistory(state, action.token, action.chart, action.sourceAction);

    case ChartSwitchActions.replaceFromVizStorage.type:
      return pushToHistory(state, action.token, action.snapshot, action.type);
    case ChartActions.updateChartTemp.type:
    case ChartActions.updateChartNoEncodingTemp.type:
      if (action.noUndo) {
        /**
         * This is for cases where an action that updates the chart happens without user interaction (i.e. backfills)
         * We need to be able to undo back to this point, but don't want to add something to the stack.
         */
        return state.set(action.token, replaceLastHistory(state.get(action.token), action.chart));
      } else {
        return pushToHistory(state, action.token, action.chart, action.description);
      }

    case ChartActions.undo.type: // fallthrough
    case ChartActions.undoTemp.type:
    case ChartActions.replaceChartHistory.type:
    case ChartSwitchActions.undo.type:
    case ChartSwitchActions.undoTemp.type:
    case ChartSwitchActions.replaceChartHistory.type:
      return state.set(action.token, popUndoStack(state.get(action.token)));

    case ChartActions.redo.type: // fallthrough
    case ChartActions.redoTemp.type:
    case ChartSwitchActions.redo.type:
    case ChartSwitchActions.redoTemp.type:
      return state.set(action.token, popRedoStack(state.get(action.token)));

    case ChartActions.makeCopy.type:
    case ChartActions.makeCopyTemp.type: {
      const history = state.get(action.originalToken);
      if (history != null) {
        // Copy the history if there are no overrides
        if (action.chart == null) {
          return state.set(action.token, history);
        } else {
          // start a new history with the values in the override
          return state.set(action.token, {
            undoStack: [],
            redoStack: [],
            last: {
              ...history.last,
              ...action.chart,
            },
          });
        }
      }

      // An empty history will result in undefined behavior
      throw new Error(`No history found for ${action.originalToken}`);
    }

    // Datasets TODO: undo support
  }

  return state;
}

export function tablePagingReducer(state = initialState.tablePaging, action: Actions) {
  switch (action.type) {
    case TempActions.Type.DestroyQuerySuccess: {
      return immRemoveKey(state, action.token);
    }
    case ChartActions.setOffset.type:
      return {
        ...state,
        [action.token]: {
          ...state[action.token],
          offset: action.offset,
        },
      };
    case ChartActions.resetOffset.type:
      return {
        ...state,
        [action.token]: {
          ...state[action.token],
          offset: 0,
        },
      };
    case ChartActions.setPageSize.type:
      return {
        ...state,
        [action.token]: {
          ...state[action.token],
          pageSize: action.pageSize,
          offset: 0,
        },
      };
    case ChartActions.loadChartsFromSwitchViewSuccess.type: {
      return action.charts.reduce(
        (stateObj, c) => {
          stateObj[c.token] = {
            pageSize: c.format?.flatTable?.general?.pageSize ?? DEFAULT_FLAMINGO_PAGE_SIZE,
            offset: 0,
          };
          return stateObj;
        },
        { ...state }
      );
    }
    case ChartActions.loadChartsSuccess.type:
    case ChartActions.loadReportViewChartSuccess.type: // fallthrough
      return action.payload.reduce(
        (stateObj, c) => {
          stateObj[c.token] = {
            pageSize: c.view_vegas.format?.flatTable?.general?.pageSize ?? DEFAULT_FLAMINGO_PAGE_SIZE,
            offset: 0,
          };
          return stateObj;
        },
        { ...state }
      );
    case ChartActions.updateChart.type:
    case ChartActions.updateChartTemp.type:
    case ChartActions.updateChartNoEncoding.type:
    case ChartActions.updateChartNoEncodingTemp.type:
    case ChartSwitchActions.updateChart.type:
    case ChartSwitchActions.updateChartNoEncoding.type:
      return {
        ...state,
        [action.token]: {
          ...state[action.token],
          pageSize: action.chart?.format?.flatTable?.general?.pageSize ?? DEFAULT_FLAMINGO_PAGE_SIZE,
          offset: 0,
        },
      };
  }

  return state;
}

export function tableColumnSearchReducer(state = initialState.tableColumnSearch, action: Actions) {
  switch (action.type) {
    case ChartActions.setTableColumnSearch.type:
      return {
        [action.token]: action.columnSearchModel,
      };
  }

  return state;
}

export const normalizedChartsReducer = {
  selectedToken: selectedTokenReducer,

  tokens: tokensReducer,
  versions: versionsReducer,
  queryTokens: queryTokensReducer,
  switchViewTokens: switchViewTokensReducer,
  resources: resourcesReducer,
  createdTimestamps: createdTimestampsReducer,

  titles: titlesReducer,
  descriptions: descriptionsReducer,
  captions: captionsReducer,
  chartTypes: chartTypesReducer,
  encodings: encodingsReducer,
  formats: formatsReducer,
  fieldFormats: fieldFormatsReducer,
  fieldColors: fieldColorsReducer,
  facetSizes: facetSizeReducer,
  colorPaletteTokens: colorPaletteTokensReducer,
  isAutoUpdateDisabled: isAutoUpdateDisabledReducer,
  manuallyApplyChanges: manuallyApplyChangesReducer,
  unappliedChangesCount: unappliedChangesCountReducer,
  ephemerals: ephemeralsReducer,

  v1Views: v1ViewsReducer,
  histories: historiesReducer,

  tablePaging: tablePagingReducer,
  tableColumnSearch: tableColumnSearchReducer,

  selectedFieldToFormat: selectedFieldToFormatReducer,
};

/**
 * Our state is composed of a map of action reducer functions.
 * These reducer functions are called with each dispatched action
 * and the current or initial state and return a new immutable state.
 */
export const reducer = new InjectionToken<ActionReducerMap<ChartFeatureState, Actions>>('chart reducers token', {
  factory: () => ({
    selectedToken: selectedTokenReducer,
    tokens: tokensReducer,
    versions: versionsReducer,
    queryTokens: queryTokensReducer,
    switchViewTokens: switchViewTokensReducer,
    resources: resourcesReducer,
    createdTimestamps: createdTimestampsReducer,
    titles: titlesReducer,
    descriptions: descriptionsReducer,
    captions: captionsReducer,
    chartTypes: chartTypesReducer,
    encodings: encodingsReducer,
    formats: formatsReducer,
    fieldFormats: fieldFormatsReducer,
    fieldColors: fieldColorsReducer,
    colorPaletteTokens: colorPaletteTokensReducer,
    facetSizes: facetSizeReducer,
    isAutoUpdateDisabled: isAutoUpdateDisabledReducer,
    manuallyApplyChanges: manuallyApplyChangesReducer,
    unappliedChangesCount: unappliedChangesCountReducer,
    ephemerals: ephemeralsReducer,

    v1Views: v1ViewsReducer,
    histories: historiesReducer,

    tablePaging: tablePagingReducer,
    tableColumnSearch: tableColumnSearchReducer,

    selectedFieldToFormat: selectedFieldToFormatReducer,
  }),
});
